import axios, { AxiosRequestConfig, AxiosResponse } from "axios";

interface RetryConfig {
  maxRetries: number;
  retryDelay: number; // Base delay in milliseconds
  maxRetryDelay?: number; // Maximum delay cap in milliseconds
  backoffFactor?: number; // Multiplier for each retry attempt
  jitter?: boolean; // Whether to add randomness to the delay
  retryStatusCodes?: number[];
}

const defaultRetryConfig: RetryConfig = {
  maxRetries: 5,
  retryDelay: 1000, // 1 second base delay
  maxRetryDelay: 30000, // 30 seconds maximum delay
  backoffFactor: 2, // Double the delay on each retry
  jitter: true, // Add randomness to prevent thundering herd
  retryStatusCodes: [
    // Client Errors
    408, // Request Timeout
    425, // Too Early
    429, // Too Many Requests

    // Server Errors
    500, // Internal Server Error
    502, // Bad Gateway
    503, // Service Unavailable
    504, // Gateway Timeout
    506, // Variant Also Negotiates
    507, // Insufficient Storage
    508, // Loop Detected
    509, // Bandwidth Limit Exceeded
    510, // Not Extended
    511, // Network Authentication Required

    // Other Errors
    520, // Unknown Error
    521, // Web Server is Down
    522, // Connection Timed Out
    523, // Origin is Unreachable
    524, // A Timeout Occurred
    525, // SSL Handshake Failed
    526, // Invalid SSL Certificate
    527, // Railgun Error
    530 // Origin DNS Error
  ]
};

export async function axiosWithRetry<T>(
  url: string,
  method: "get" | "post" | "put" | "delete" | "patch",
  options: AxiosRequestConfig = {},
  retryConfig: Partial<RetryConfig> = {}
): Promise<AxiosResponse<T>> {
  const config: RetryConfig = { ...defaultRetryConfig, ...retryConfig };
  let retries = 0;

  while (true) {
    try {
      return await axios[method](url, options.data, options);
    } catch (error: unknown) {
      const axiosError = error as import("axios").AxiosError;

      // Handle network errors (when internet is disconnected)
      const isNetworkError =
        axiosError.code &&
        [
          "ECONNABORTED",
          "ECONNREFUSED",
          "ECONNRESET",
          "ETIMEDOUT",
          "ERR_NETWORK",
          "ENETUNREACH"
        ].includes(axiosError.code);

      const shouldRetryStatusCode =
        axiosError.response &&
        config.retryStatusCodes?.includes(axiosError.response.status);

      if (
        retries >= config.maxRetries ||
        (!isNetworkError && !shouldRetryStatusCode)
      ) {
        throw error;
      }

      retries++;

      // Calculate exponential backoff delay
      let delay =
        config.retryDelay * (config.backoffFactor || 2) ** (retries - 1);

      // Apply maximum delay cap if configured
      if (config.maxRetryDelay) {
        delay = Math.min(delay, config.maxRetryDelay);
      }

      // Add jitter to prevent thundering herd problem
      if (config.jitter) {
        // Add random jitter between -25% and +25% of the delay
        const jitterFactor = 0.25; // 25% jitter
        const jitterAmount = delay * jitterFactor;
        delay = delay + Math.random() * jitterAmount * 2 - jitterAmount;
      }

      console.log(
        `Retry attempt ${retries}/${config.maxRetries} for ${url} after ${Math.round(delay)}ms${isNetworkError ? " (Network error)" : ""}`
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
}
